import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, Building2, ChartColumn, ChevronsUpDown, Gauge, LogOut, Menu, Moon, Search, Settings, Sun, User, Waves, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useAssistant } from "@/context/AssistantContext";
import { useApi } from "@/hooks/use-api";
import { AskPanel } from "@/components/ai/AskPanel";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { UserAvatar } from "@/components/common/UserAvatar";
import { LensMark, ProximaLogo } from "@/components/brand/ProximaLogo";
import { ROLE_LABEL } from "@/lib/status";
import { cn } from "@/lib/utils";

/* The shell: one sidebar, no separate top bar on desktop. Search lives at the top of the sidebar (⌘K kept),
   the assistant has one fixed entry, and alerts are the count next to «Hendelser». The work surface starts at the top. */
export const SIDEBAR_WIDTH = 224;

export const NAV = [
  { to: "/", label: "Oversikt", icon: Gauge, end: true, testId: "nav-oversikt" },
  { to: "/kraftverk", label: "Kraftverk", icon: Waves, testId: "nav-kraftverk" },
  { to: "/hendelser", label: "Hendelser", icon: Bell, testId: "nav-hendelser" },
  { to: "/rapporter", label: "Rapporter", icon: ChartColumn, testId: "nav-rapporter" },
  { to: "/service", label: "Service", icon: Wrench, testId: "nav-service" },
];
const AskIcon = (props) => <LensMark tone="mono" {...props} />;
export const ASK_NAV = { to: "/ai", label: "Spør HydroHub", icon: AskIcon, testId: "nav-ai" };
export const SETTINGS_NAV = { to: "/innstillinger", label: "Innstillinger", icon: Settings, testId: "nav-innstillinger" };
export const ADMIN_NAV = { to: "/admin", label: "Alle kunder", icon: Building2, testId: "nav-admin" };

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
const MOD = isMac ? "⌘" : "Ctrl";

/* Customer context directly under the logo – the "workspace" row. Static for customers, a selector for Proxima staff. */
const CustomerMark = ({ name, all }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] bg-primary text-[13px] font-semibold text-primary-foreground" aria-hidden="true">
    {all ? <Building2 className="h-4 w-4" strokeWidth={2} /> : (name || "?").trim()[0]?.toUpperCase()}
  </span>
);

const CustomerContext = ({ onNavigate, plantsTotal }) => {
  const { isProxima, customers, activeCustomerId, setActiveCustomerId, customer } = useAuth();
  const navigate = useNavigate();
  const label = customer?.short_name || customer?.name || (isProxima ? "Alle kunder" : "–");
  const sub = customer ? (plantsTotal ? `${plantsTotal} kraftverk` : null) : isProxima ? `${customers.length} kunder` : null;
  const body = (
    <>
      <CustomerMark name={label} all={isProxima && !customer} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] font-semibold leading-[1.2] text-foreground" data-testid="active-customer-name">
          {label}
        </span>
        {sub ? <span className="metric block truncate text-[12px] leading-[1.2] text-muted-foreground">{sub}</span> : null}
      </span>
    </>
  );
  if (!isProxima) {
    return (
      <div className="flex h-11 items-center gap-3 px-2.5" title={customer?.name} data-testid="active-customer">
        {body}
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-11 w-full items-center gap-3 rounded-md px-2.5 text-left transition-[background-color] duration-150 hover:bg-rail-hover data-[state=open]:bg-rail-hover" title={customer?.name} data-testid="customer-switcher">
          {body}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[248px]">
        <DropdownMenuLabel className="text-[13px] font-medium text-muted-foreground">Vis som kunde</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activeCustomerId || ""}
          onValueChange={(v) => {
            setActiveCustomerId(v);
            navigate("/");
            onNavigate?.();
          }}
        >
          {customers.map((c) => (
            <DropdownMenuRadioItem key={c.id} value={c.id} data-testid={`customer-option-${c.key}`}>
              {c.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            navigate("/admin");
            onNavigate?.();
          }}
          data-testid="customer-switcher-admin"
        >
          <Building2 className="mr-1 h-4 w-4" aria-hidden="true" /> Alle kunder (Proxima)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ProfileMenu = ({ variant = "rail" }) => {
  const { user, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "rail" ? (
          <button type="button" className="flex h-12 w-full items-center gap-2.5 rounded-md px-2 text-left transition-[background-color] duration-150 hover:bg-rail-hover data-[state=open]:bg-rail-hover" aria-label="Profilmeny" data-testid="profile-menu-trigger">
            <UserAvatar user={user} size="md" testId="profile-avatar" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-medium leading-[1.25]">{user?.name}</span>
              <span className="block truncate text-[12.5px] leading-[1.25] text-muted-foreground">{user?.title || ROLE_LABEL[user?.role] || user?.role}</span>
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        ) : (
          <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-full" aria-label="Profilmeny" data-testid="profile-menu-trigger">
            <UserAvatar user={user} size="md" testId="profile-avatar" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={variant === "rail" ? "start" : "end"} side={variant === "rail" ? "top" : "bottom"} className="w-[248px]">
        <DropdownMenuLabel className="flex items-center gap-3 py-2">
          <UserAvatar user={user} size="lg" testId="profile-menu-avatar" />
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-medium" data-testid="profile-menu-name">
              {user?.name}
            </span>
            <span className="block truncate text-[12.5px] font-normal text-muted-foreground">{user?.email}</span>
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/innstillinger")} data-testid="profile-menu-settings">
          <User className="mr-1 h-4 w-4" aria-hidden="true" /> Profil og innstillinger
        </DropdownMenuItem>
        <DropdownMenuItem onClick={toggle} data-testid="theme-toggle">
          {isDark ? <Sun className="mr-1 h-4 w-4" aria-hidden="true" /> : <Moon className="mr-1 h-4 w-4" aria-hidden="true" />}
          {isDark ? "Lyst tema" : "Mørkt tema"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive" data-testid="logout-button">
          <LogOut className="mr-1 h-4 w-4" aria-hidden="true" /> Logg ut
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const RailLink = ({ item, onClick, trailing }) => {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end} onClick={onClick} data-testid={item.testId} className={({ isActive }) => cn("rail-link", isActive && "active")}>
      <Icon className="h-[17px] w-[17px] shrink-0" strokeWidth={1.75} aria-hidden="true" />
      <span className="flex-1 truncate">{item.label}</span>
      {trailing}
    </NavLink>
  );
};

/* the alert count next to «Hendelser» – red only when something is not yet acknowledged */
const EventsCount = ({ active, unack }) => {
  if (!active) return null;
  return (
    <span className={cn("metric text-[13px] tabular-nums", unack ? "font-semibold text-rose-700 dark:text-rose-300" : "text-muted-foreground")} data-testid="nav-hendelser-badge" title={unack ? `${unack} ikke kvittert` : undefined}>
      {active}
    </span>
  );
};

const SearchButton = ({ onClick }) => (
  <button type="button" onClick={onClick} className="rail-search" data-testid="sidebar-search-button">
    <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
    <span className="flex-1 truncate">Søk</span>
    <span className="kbd">{MOD}K</span>
  </button>
);

const Sidebar = ({ onNavigate, onSearch, eventsActive, eventsUnack, plantsTotal }) => {
  const { isProxima } = useAuth();
  return (
    <div className="rail flex h-full flex-col border-r border-rail-border" data-testid="sidebar-inner">
      <div className="px-5 pt-5">
        <Link to="/" onClick={onNavigate} className="inline-block rounded-sm" aria-label="Til oversikten">
          <ProximaLogo tagline="HydroHub" className="text-[15px]" />
        </Link>
      </div>
      <div className="mt-5 px-3">
        <CustomerContext onNavigate={onNavigate} plantsTotal={plantsTotal} />
      </div>
      <div className="mt-3 px-3">
        <SearchButton onClick={onSearch} />
      </div>
      <nav className="mt-5 flex-1 overflow-y-auto px-3" aria-label="Hovedmeny">
        <div className="space-y-0.5">
          {NAV.map((item) => (
            <RailLink key={item.to} item={item} onClick={onNavigate} trailing={item.to === "/hendelser" ? <EventsCount active={eventsActive} unack={eventsUnack} /> : null} />
          ))}
        </div>
        <div className="mt-6 space-y-0.5">
          <RailLink item={ASK_NAV} onClick={onNavigate} />
        </div>
      </nav>
      <div className="space-y-0.5 px-3 pb-2">
        {isProxima ? <RailLink item={ADMIN_NAV} onClick={onNavigate} /> : null}
        <RailLink item={SETTINGS_NAV} onClick={onNavigate} />
      </div>
      <div className="px-3 pb-3 pt-1">
        <ProfileMenu variant="rail" />
      </div>
    </div>
  );
};

const MobileNav = ({ onOpenAsk }) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const { isProxima } = useAuth();
  const primary = NAV.slice(0, 3);
  const more = [...NAV.slice(3), SETTINGS_NAV];
  const moreActive = more.some((m) => location.pathname.startsWith(m.to)) || location.pathname.startsWith("/admin");
  const itemCls = (active) => cn("relative flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors", active ? "text-primary" : "text-muted-foreground");
  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/95 backdrop-blur-md print:hidden lg:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} aria-label="Mobilmeny" data-testid="mobile-nav">
        <div className="grid grid-cols-5">
          {primary.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={`mobile-${item.testId}`} className={({ isActive }) => itemCls(isActive)}>
                <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                {item.label}
              </NavLink>
            );
          })}
          <button type="button" onClick={onOpenAsk} className={itemCls(location.pathname.startsWith("/ai"))} data-testid="mobile-ask-button">
            <LensMark className="h-5 w-5" tone="mono" />
            Spør
          </button>
          <button type="button" onClick={() => setMoreOpen(true)} className={itemCls(moreActive)} data-testid="mobile-nav-more">
            <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
            Mer
          </button>
        </div>
      </nav>
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="p-4">
          <SheetHeader>
            <SheetTitle className="text-[16px]">Meny</SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-px">
            {more.map((item) => (
              <RailLink key={item.to} item={{ ...item, testId: `mobile-${item.testId}` }} onClick={() => setMoreOpen(false)} />
            ))}
            {isProxima ? <RailLink item={{ ...ADMIN_NAV, label: "Proxima – alle kunder", testId: "mobile-nav-admin" }} onClick={() => setMoreOpen(false)} /> : null}
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <CustomerContext onNavigate={() => setMoreOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export const AppShell = () => {
  const [cmdOpen, setCmdOpen] = useState(false);
  const { openWith } = useAssistant();
  const location = useLocation();
  const summary = useApi("/portfolio/summary", {}, { staleTime: 120_000 });
  const eventsActive = summary.data?.kpis?.events_active ?? summary.data?.kpis?.events_open ?? 0;
  const eventsUnack = summary.data?.kpis?.events_unacknowledged ?? 0;
  const fullBleed = location.pathname.startsWith("/ai");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openCommand = () => setCmdOpen(true);
  const openAsk = () => openWith(null, {});

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-40 hidden print:hidden lg:block" style={{ width: SIDEBAR_WIDTH }} data-testid="sidebar">
        <Sidebar onSearch={openCommand} eventsActive={eventsActive} eventsUnack={eventsUnack} plantsTotal={summary.data?.kpis?.plants_total} />
      </aside>

      <div className="print:pl-0 lg:pl-[224px]">
        {/* Mobile only: logo, search, profile. On desktop the sidebar carries all of this and the page starts at the top. */}
        <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-md print:hidden lg:hidden" data-testid="topbar">
          <div className="flex h-[52px] items-center justify-between gap-3 px-4 sm:px-6">
            <Link to="/" className="flex items-center" aria-label="Til oversikten">
              <ProximaLogo tagline="HydroHub" className="text-[14px]" />
            </Link>
            <div className="flex items-center gap-1">
              <Button variant="quiet" size="icon" onClick={openCommand} aria-label="Søk" data-testid="topbar-search-button">
                <Search className="h-[18px] w-[18px]" />
              </Button>
              <ProfileMenu variant="top" />
            </div>
          </div>
        </header>

        {fullBleed ? (
          <main className="relative" data-testid="main-fullbleed">
            <Outlet />
          </main>
        ) : (
          <main className="relative pb-24 print:pb-0 lg:pb-10">
            <div className="page-container relative animate-fade-up py-6 print:max-w-none print:px-0 print:py-0 lg:py-8" key={location.pathname}>
              <Outlet />
            </div>
            <footer className="page-container relative mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4 text-[12.5px] text-muted-foreground print:hidden">
              <span>Prototype med fiktive eksempeldata · kun lesetilgang</span>
              <span>© {new Date().getFullYear()} Proxima Hydrotech AS</span>
            </footer>
          </main>
        )}
      </div>

      <MobileNav onOpenAsk={openAsk} />
      <AskPanel />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  );
};
